import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  User, 
  Mail, 
  Phone,
  MessageSquare,
  Plus,
  AlertCircle,
  Edit,
  Trash
} from 'lucide-react';
import { fetchClientById, addNote, updateClientStatus, deleteNote, updateNextSessionDate, fetchNextClientSession } from '../../lib/supabase';
import DocumentList from './DocumentList';
import StatusCompletionModal from './StatusCompletionModal';
import SessionManagement from './SessionManagement';

const NotesSection = ({ notes, onDelete, onEdit }) => {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  

  const handleEdit = async (noteId, content) => {
    try {
      await onEdit(noteId, content);
      setEditingNoteId(null);
    } catch (err) {
      console.error('Error editing note:', err);
    }
  };

  const confirmDelete = (noteId) => {
    setSelectedNoteId(noteId);
    setConfirmOpen(true);
  };

  const handleDelete = () => {
    onDelete(selectedNoteId);
    setConfirmOpen(false);
    setSelectedNoteId(null);
  };

  if (!notes || notes.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        No notes available for this client
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notes.map((note) => (
        <div 
          key={note.id} 
          className={`border-l-4 pl-4 ${
            note.type === 'initial' 
              ? 'border-yellow-500 bg-yellow-50 rounded-r-md' 
              : 'border-teal-500'
          }`}
        >
          {editingNoteId === note.id ? (
            <div className="space-y-3 p-4">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                rows={4}
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setEditingNoteId(null)}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleEdit(note.id, editedContent)}
                  className="px-3 py-1 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between p-4">
                <div className="flex items-center">
                  <MessageSquare className={`h-5 w-5 ${
                    note.type === 'initial' ? 'text-yellow-500' : 'text-teal-500'
                  } mr-2`} />
                  <span className="text-sm text-gray-500">
                    {new Date(note.created_at).toLocaleDateString()}
                    {note.type === 'initial' && (
                      <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                        Initial Notes
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingNoteId(note.id);
                      setEditedContent(note.content);
                    }}
                    className="text-teal-600 hover:text-teal-700 text-sm"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => confirmDelete(note.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    <Trash className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-gray-700 whitespace-pre-wrap px-4 pb-4">{note.content}</p>
            </>
          )}
        </div>
      ))}

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white p-6 rounded shadow-lg relative z-10 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this note?</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ClientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isDeleteSessionConfirmOpen, setIsDeleteSessionConfirmOpen] = useState(false);
  const [isStatusCompletionModalOpen, setIsStatusCompletionModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [nextSession, setNextSession] = useState(null);

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
      setClient(prev => ({
        ...prev,
        notes: prev.notes.filter(note => note.id !== noteId)
      }));
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const fetchNextSession = useCallback(async () => {
    try {
      const sessionData = await fetchNextClientSession(id);
      setNextSession(sessionData);
    } catch (err) {
      console.error('Error fetching next session:', err);
    }
  }, [id]);

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const formatGradeYear = (gradeYear, clientType) => {
    if (!gradeYear) return '';
    
    // For school students, format as "Xth Grade" if it's just a number
    if (
      (clientType === 'below_8th' || clientType === '8th_10th' || clientType === 'puc') && 
      /^[0-9]+$/.test(gradeYear)
    ) {
      // Handle special cases for ordinal suffixes
      let suffix = "th";
      if (gradeYear.endsWith('1') && !gradeYear.endsWith('11')) suffix = "st";
      if (gradeYear.endsWith('2') && !gradeYear.endsWith('12')) suffix = "nd";
      if (gradeYear.endsWith('3') && !gradeYear.endsWith('13')) suffix = "rd";
      
      return `${gradeYear}${suffix} Grade`;
    }
    
    return gradeYear;
  };

  const sortNotes = (notes) => {
    if (!notes || !Array.isArray(notes)) return [];
    
    // Create a copy of the notes array to avoid mutating the original
    return [...notes].sort((a, b) => {
      // First sort by type: 'initial' notes should appear first
      if (a.type === 'initial' && b.type !== 'initial') return -1;
      if (a.type !== 'initial' && b.type === 'initial') return 1;
      
      // Then sort by creation date (newest first for session notes)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };
  
  const handleEditNote = async (noteId, content) => {
    try {
      setClient(prev => ({
        ...prev,
        notes: prev.notes.map(note => 
          note.id === noteId ? { ...note, content } : note
        )
      }));
    } catch (err) {
      console.error('Error editing note:', err);
    }
  };

  useEffect(() => {
    const loadClientProfile = async () => {
      try {
        setLoading(true);
        const data = await fetchClientById(id);
        
        // Sort notes if they exist
        if (data && data.notes) {
          data.notes = sortNotes(data.notes);
        }
        
        setClient(data);
        
        // Fetch the next session
        await fetchNextSession();
      } catch (err) {
        console.error('Error loading client:', err);
        setError('Failed to load client details');
      } finally {
        setLoading(false);
      }
    };

    loadClientProfile();
  }, [id, fetchNextSession]);

  const StatusDropdown = ({ currentStatus, onStatusChange }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'active':
          return 'text-green-800 bg-green-100 border-green-200';
        case 'completed':
          return 'text-blue-800 bg-blue-100 border-blue-200';
        case 'on-hold':
          return 'text-yellow-800 bg-yellow-100 border-yellow-200';
        case 'cancelled':
          return 'text-red-800 bg-red-100 border-red-200';
        default:
          return 'text-gray-800 bg-gray-100 border-gray-200';
      }
    };
  
    return (
      <select
        value={currentStatus}
        onChange={(e) => onStatusChange(e.target.value)}
        className={`px-3 py-1 rounded-full text-sm border-2 transition-colors cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 ${getStatusColor(currentStatus)}`}
      >
        <option value="active">Active</option>
        <option value="completed">Completed</option>
        <option value="on-hold">On Hold</option>
        <option value="cancelled">Cancelled</option>
      </select>
    );
  };

  const handleStatusChange = async (newStatus) => {
    // Check if trying to set to 'completed'
    if (newStatus === 'completed') {
      // Check if there's a scheduled session that hasn't happened yet
      if (client.next_session_date) {
        const sessionDate = new Date(client.next_session_date);
        const now = new Date();
        
        if (sessionDate > now) {
          showToastNotification('Cannot mark as completed: scheduled session has not occurred yet');
          return;
        }
      }
      
      // Open the confirmation modal and set the pending status
      setPendingStatusChange(newStatus);
      setIsStatusCompletionModalOpen(true);
      return;
    }
    
    // For non-completed statuses, apply the change immediately
    try {
      await updateClientStatus(id, newStatus);
      setClient(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleStatusConfirm = async () => {
    try {
      await updateClientStatus(id, pendingStatusChange);
      setClient(prev => ({ ...prev, status: pendingStatusChange }));
      setIsStatusCompletionModalOpen(false);
      setPendingStatusChange(null);
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };
  

  const getClientTypeLabel = (type) => {
    const typeMap = {
      'below_8th': 'Below 8th Grade',
      '8th_10th': '8th to 10th Grade',
      'puc': 'PUC (11th & 12th)',
      'college': 'College Student',
      'professional': 'Professional'
    };
    return typeMap[type] || type;
  };
  

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
  
    setIsSubmittingNote(true);
    try {
      const newNoteData = await addNote({
        client_id: id,
        content: newNote,
        type: 'session'
      });
      
      setClient(prev => ({
        ...prev,
        notes: [...(prev.notes || []), newNoteData]
      }));
      setNewNote('');
      setIsAddingNote(false);
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleDeleteSession = async () => {
    try {
      await updateNextSessionDate(client.id, null);
      setClient(prev => ({ ...prev, next_session_date: null }));
      setIsDeleteSessionConfirmOpen(false);
      showToastNotification('Session deleted successfully');
    } catch (err) {
      console.error('Error deleting session:', err);
      showToastNotification('Failed to delete session');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center p-4">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-lg text-red-600">{error || 'Client not found'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/admin/dashboard/clients')}
          className="flex items-center text-teal-600 hover:text-teal-700 mb-8"
        >
          <ChevronLeft size={20} className="mr-1" />
          Back to Clients
        </button>
        
        <div className="flex justify-between items-start">
        
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
          <p className="text-gray-600 mb-2">
            {getClientTypeLabel(client.type)} 
            {client.grade_year ? ` • ${formatGradeYear(client.grade_year, client.type)}` : ''}
          </p>
          <span className='text-teal-600 font-bold'>Registration Number: {client.registration_number}</span>
          {client.status !== 'cancelled' ? (
            <div className="flex items-center">
              <p className="font-medium mt-1 text-teal-600"> Next Session:
                <span className='ml-1 text-red-600'>
                  {nextSession ? 
                    new Date(nextSession.session_date).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    }) : 
                    'Not scheduled'
                  }
                </span>
              </p>
              {nextSession && (
                <div className="ml-2 text-teal-700">
                  ({nextSession.session_type})
                </div>
              )}
            </div>
          ) : (
            <p className="font-medium mt-1 text-gray-500">
              <span className='italic'>No sessions available - client cancelled</span>
            </p>
          )}
        </div>
        <div className="flex items-center">
          <StatusDropdown 
            currentStatus={client.status} 
            onStatusChange={handleStatusChange}
          />
        </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Client Info */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6 border border-teal-500">
            <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <span>{client.email}</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-gray-400 mr-3" />
                <span>{client.phone}</span>
              </div>
              {client.type === 'school' && client.parent_contact && (
                <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <span>Parent: {client.parent_contact}</span>
                </div>
                )}
            </div>
          </div>

          <DocumentList 
            clientId={id}
            documents={client.documents}
            onDocumentChange={fetchNextSession}
          />
        </div>

        {/* Right Column - Reordered Sections */}
        <div className="lg:col-span-2 space-y-6">
          {/* Use the new SessionManagement component */}
          <SessionManagement 
            clientId={id} 
            clientName={client.name}
            onSessionChange={fetchNextSession}
          />

          {/* Session Notes - Moved below Upcoming Sessions */}
          <div className="bg-white rounded-lg shadow p-6 border border-teal-500">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">Notes</h2>
              <button
                onClick={() => setIsAddingNote(true)}
                className="flex items-center px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                <Plus size={18} className="mr-1" />
                Add Note
              </button>
            </div>

            {isAddingNote && (
              <form onSubmit={handleAddNote} className="mb-6">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter session notes..."
                  className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  rows={4}
                />
                <div className="mt-3 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNote('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingNote}
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
                  >
                    {isSubmittingNote ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-6">
            <NotesSection 
                notes={client.notes}
                onDelete={handleDeleteNote}
                onEdit={handleEditNote}
            />

              {(!client.notes || client.notes.length === 0) && (
                <div className="text-center py-6 text-gray-500">
                  No session notes yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Session Confirmation Modal */}
      {isDeleteSessionConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white p-6 rounded shadow-lg relative z-10 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Delete Session</h2>
            <p className="mb-4">Are you sure you want to delete this scheduled session?</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsDeleteSessionConfirmOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSession}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-down">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{toastMessage}</span>
            </div>
          </div>
        </div>
      )}
      {isStatusCompletionModalOpen && (
        <StatusCompletionModal
          isOpen={isStatusCompletionModalOpen}
          onClose={() => {
            setIsStatusCompletionModalOpen(false);
            setPendingStatusChange(null);
          }}
          onConfirm={handleStatusConfirm}
          clientName={client.name}
        />
      )}
    </div>
  );
};

export default ClientProfile;